import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { RefreshCw, Search, ArrowUpRight, Calendar, Filter, Download, Store, ShoppingBag, CheckCircle, Clock, FileSpreadsheet, RotateCcw, X, User, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

const API = "/api";

export default function OrdersPage() {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [transferring, setTransferring] = useState(false);
  const [activeTab, setActiveTab] = useState("shopify");
  
  // Shopify Orders State
  const [shopifyOrders, setShopifyOrders] = useState([]);
  const [shopifyLoading, setShopifyLoading] = useState(false);
  const [shopifyTotal, setShopifyTotal] = useState(0);
  const [shopifyPage, setShopifyPage] = useState(1);
  const [shopifyTotalPages, setShopifyTotalPages] = useState(1);
  const [shopifyStores, setShopifyStores] = useState([]);
  const [shopifyStats, setShopifyStats] = useState(null);
  const [selectedShopifyOrders, setSelectedShopifyOrders] = useState([]);
  const [syncingToNebim, setSyncingToNebim] = useState(false);
  const [shopifySelectedStore, setShopifySelectedStore] = useState('');
  const [shopifySearchQuery, setShopifySearchQuery] = useState('');
  const [pullStartDate, setPullStartDate] = useState('');
  const [pullEndDate, setPullEndDate] = useState('');
  const [shopifySortColumn, setShopifySortColumn] = useState('created_at_shopify');
  const [shopifySortDirection, setShopifySortDirection] = useState('desc');
  
  const [filters, setFilters] = useState({
    status: "",
    package_status: "",
    payment_type: "",
    source: "",
    start_date: "",
    end_date: "",
    transferred: null,
  });

  useEffect(() => {
    if (activeTab === 'shopify') {
      fetchShopifyOrders();
      fetchShopifyStats();
    } else {
      fetchOrders();
    }
  }, [token, page, filters, activeTab, shopifyPage, shopifySelectedStore, shopifySearchQuery]);

  // Shopify Orders Functions
  const fetchShopifyOrders = async () => {
    setShopifyLoading(true);
    try {
      const params = new URLSearchParams({ token, page: shopifyPage, limit: 50 });
      if (shopifySelectedStore) params.append('store_id', shopifySelectedStore);
      if (shopifySearchQuery) params.append('search', shopifySearchQuery);
      
      const response = await axios.get(`${API}/shopify-orders?${params}`);
      setShopifyOrders(response.data.orders || []);
      setShopifyTotal(response.data.total || 0);
      setShopifyTotalPages(response.data.total_pages || 1);
      setShopifyStores(response.data.stores || []);
    } catch (error) {
      toast.error('Shopify siparişleri yüklenemedi');
    } finally {
      setShopifyLoading(false);
    }
  };

  const fetchShopifyStats = async () => {
    try {
      const response = await axios.get(`${API}/shopify-orders/stats?token=${token}`);
      setShopifyStats(response.data);
    } catch (error) {
      console.error('Stats error:', error);
    }
  };

  const pullAndSyncOrders = async () => {
    setSyncingToNebim(true);
    try {
      toast.info('Siparişler çekiliyor ve aktarılıyor...');
      const params = new URLSearchParams({ token });
      if (pullStartDate) params.append('start_date', pullStartDate);
      if (pullEndDate) params.append('end_date', pullEndDate);
      
      const response = await axios.post(`${API}/shopify-orders/pull-and-sync?${params}`, {}, { timeout: 1200000 });
      
      if (response.data.success) {
        const { pulled, synced } = response.data;
        if (pulled > 0 || synced > 0) {
          toast.success(`${pulled} sipariş çekildi, ${synced} sipariş Nebim'e aktarıldı`);
        } else {
          toast.info('Yeni sipariş bulunamadı');
        }
        fetchShopifyOrders();
        fetchShopifyStats();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Çek ve aktar hatası');
    } finally {
      setSyncingToNebim(false);
    }
  };

  const syncSelectedToNebim = async () => {
    if (selectedShopifyOrders.length === 0) {
      toast.error('Sipariş seçilmedi');
      return;
    }
    setSyncingToNebim(true);
    try {
      const response = await axios.post(`${API}/shopify-orders/sync-to-nebim?token=${token}&force=true`, { order_ids: selectedShopifyOrders }, { timeout: 600000 });
      if (response.data.success) {
        toast.success(`${response.data.synced} sipariş Nebim'e aktarıldı`);
        setSelectedShopifyOrders([]);
        fetchShopifyOrders();
        fetchShopifyStats();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nebim aktarım hatası');
    } finally {
      setSyncingToNebim(false);
    }
  };

  const unmarkSingleOrder = async (orderId) => {
    try {
      const response = await axios.post(`${API}/shopify-orders/unmark-synced?token=${token}`, { order_ids: [orderId] });
      if (response.data.success) {
        toast.success('İşaret kaldırıldı');
        fetchShopifyOrders();
        fetchShopifyStats();
      }
    } catch (error) {
      toast.error('İşaret kaldırma hatası');
    }
  };

  const exportToExcel = async () => {
    toast.info('Tüm siparişler hazırlanıyor...');
    
    try {
      // Tüm siparişleri backend'den çek
      let allOrders = [];
      let currentPage = 1;
      let hasMore = true;
      
      while (hasMore) {
        const params = new URLSearchParams({ token, page: currentPage, limit: 500 });
        if (shopifySelectedStore) params.append('store_id', shopifySelectedStore);
        if (shopifySearchQuery) params.append('search', shopifySearchQuery);
        
        const response = await axios.get(`${API}/shopify-orders?${params}`);
        const fetchedOrders = response.data.orders || [];
        allOrders = [...allOrders, ...fetchedOrders];
        
        if (fetchedOrders.length < 500) {
          hasMore = false;
        } else {
          currentPage++;
          if (currentPage > 20) hasMore = false;
        }
      }
      
      if (allOrders.length === 0) {
        toast.error('Disa aktarilacak siparis yok');
        return;
      }
      
      // Türkçe karakterleri ASCII'ye çevir
      const turkishToAscii = (str) => {
        const map = {
          'ç': 'c', 'Ç': 'C', 'ğ': 'g', 'Ğ': 'G', 'ı': 'i', 'I': 'I',
          'İ': 'I', 'ö': 'o', 'Ö': 'O', 'ş': 's', 'Ş': 'S', 'ü': 'u', 'Ü': 'U'
        };
        return String(str).replace(/[çÇğĞıİöÖşŞüÜ]/g, char => map[char] || char);
      };
      
      const headers = ['Magaza', 'Siparis No', 'Musteri Adi', 'E-posta', 'Tarih', 'Tutar', 'Para Birimi', 'Odeme Durumu', 'Kargo Durumu', 'Nebim Durumu'];
      const csvRows = [headers.join(';')];
      allOrders.forEach(order => {
        const row = [
          turkishToAscii(order.store_name || ''),
          order.order_name || '',
          turkishToAscii(`${order.customer?.first_name || ''} ${order.customer?.last_name || ''}`.trim()),
          order.customer?.email || '',
          formatDate(order.created_at_shopify),
          order.total_price || '0',
          order.currency || 'TRY',
          turkishToAscii(order.financial_status === 'paid' ? 'Odendi' : order.financial_status || ''),
          turkishToAscii(order.fulfillment_status === 'fulfilled' ? 'Gonderildi' : order.fulfillment_status || ''),
          order.synced_to_nebim ? 'Aktarildi' : 'Bekliyor'
        ];
        csvRows.push(row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'));
      });
      
      // UTF-8 BOM ile dosya oluştur
      const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
      const csvString = csvRows.join('\r\n');
      const encoder = new TextEncoder();
      const csvBytes = encoder.encode(csvString);
      const combined = new Uint8Array(BOM.length + csvBytes.length);
      combined.set(BOM);
      combined.set(csvBytes, BOM.length);
      
      const blob = new Blob([combined], { type: 'application/vnd.ms-excel' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `shopify_siparisler_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${allOrders.length} siparis Excel'e aktarildi`);
    } catch (error) {
      toast.error('Excel export hatasi');
      console.error(error);
    }
  };

  const handleShopifySort = (column) => {
    if (shopifySortColumn === column) {
      setShopifySortDirection(shopifySortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setShopifySortColumn(column);
      setShopifySortDirection('asc');
    }
  };

  const sortedShopifyOrders = [...shopifyOrders].sort((a, b) => {
    let aVal = a[shopifySortColumn];
    let bVal = b[shopifySortColumn];
    if (shopifySortColumn === 'total_price') {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
    }
    if (shopifySortColumn === 'created_at_shopify') {
      aVal = new Date(aVal).getTime() || 0;
      bVal = new Date(bVal).getTime() || 0;
    }
    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    if (aVal < bVal) return shopifySortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return shopifySortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const toggleShopifyOrderSelection = (orderId) => {
    setSelectedShopifyOrders(prev => prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId]);
  };

  const selectAllShopifyOrders = () => {
    if (selectedShopifyOrders.length === shopifyOrders.length) {
      setSelectedShopifyOrders([]);
    } else {
      setSelectedShopifyOrders(shopifyOrders.map(o => o.id));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatCurrency = (amount, currency = 'TRY') => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currency }).format(parseFloat(amount || 0));
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      let queryFilters = { ...filters };
      
      // Apply tab filters
      if (activeTab === "pending") {
        queryFilters.transferred = false;
      } else if (activeTab === "transferred") {
        queryFilters.transferred = true;
      } else if (activeTab === "cancelled") {
        queryFilters.status = "İptal";
      } else if (activeTab === "returns") {
        queryFilters.status = "İade";
      }
      
      const params = new URLSearchParams({ token, page, limit: 20 });
      if (queryFilters.status) params.append("status", queryFilters.status);
      if (queryFilters.package_status) params.append("package_status", queryFilters.package_status);
      if (queryFilters.start_date) params.append("start_date", queryFilters.start_date);
      if (queryFilters.end_date) params.append("end_date", queryFilters.end_date);
      if (queryFilters.transferred !== null) params.append("transferred", queryFilters.transferred);
      
      const response = await axios.get(`${API}/orders?${params}`);
      setOrders(response.data.orders);
      setTotal(response.data.total);
    } catch (error) {
      toast.error("Siparişler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const transferSelectedOrders = async () => {
    if (selectedOrders.length === 0) {
      toast.error("Lütfen sipariş seçin");
      return;
    }
    setTransferring(true);
    try {
      const response = await axios.post(`${API}/orders/transfer?token=${token}`, selectedOrders);
      toast.success(`${response.data.transferred_count} sipariş aktarıldı`);
      setSelectedOrders([]);
      fetchOrders();
    } catch (error) {
      toast.error("Siparişler aktarılamadı");
    } finally {
      setTransferring(false);
    }
  };

  const syncShopifyOrders = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/shopify/sync-orders?token=${token}`);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchOrders();
      } else {
        toast.error(response.data.error || "Senkronizasyon başarısız");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Shopify senkronizasyon hatası");
    } finally {
      setSyncing(false);
    }
  };

  const toggleOrderSelection = (orderId) => {
    setSelectedOrders((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedOrders.length === orders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(orders.map((o) => o.id));
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      "Siparişiniz Alındı": "badge-info",
      "Onay Bekliyor": "badge-warning",
      "Onaylandı": "badge-success",
      "Paketleniyor": "badge-info",
      "Kargoya Verildi": "badge-success",
      "Teslim Edildi": "badge-success",
      "İptal": "badge-error",
      "İade": "badge-warning",
    };
    return statusColors[status] || "badge-info";
  };

  const orderStatuses = [
    "Siparişiniz Alındı", "Onay Bekliyor", "Onaylandı", "Ödeme Bekliyor",
    "Paketleniyor", "Tedarik Ediliyor", "Kargoya Verildi", "Teslim Edildi",
    "İptal", "İade"
  ];

  const packageStatuses = ["Beklemede", "Paketleniyor", "Kargoya Verildi", "Fatura Kesildi"];

  return (
    <div className="space-y-6 fade-in" data-testid="orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Sipariş Yönetimi</h1>
          <p className="text-slate-500 mt-1">Shopify siparişlerini görüntüleyin ve Nebim'e aktarın</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={syncShopifyOrders}
            disabled={syncing}
            data-testid="btn-sync-shopify"
          >
            {syncing ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            {syncing ? "Çekiliyor..." : "Shopify'dan Çek"}
          </Button>
          <Button
            onClick={transferSelectedOrders}
            disabled={transferring || selectedOrders.length === 0}
            data-testid="btn-transfer-orders"
          >
            <ArrowUpRight className="w-4 h-4 mr-2" />
            {transferring ? "Aktarılıyor..." : `Nebim'e Aktar (${selectedOrders.length})`}
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800 text-white">
          <TabsTrigger value="shopify" className="data-[state=active]:bg-green-600" data-testid="tab-shopify-orders">
            <ShoppingBag className="w-4 h-4 mr-2" />
            Shopify Siparişleri
          </TabsTrigger>
          <TabsTrigger value="all" className="data-[state=active]:bg-slate-600" data-testid="tab-all-orders">Siparişler</TabsTrigger>
          <TabsTrigger value="pending" className="data-[state=active]:bg-slate-600" data-testid="tab-pending-orders">Aktarılmayan Siparişler</TabsTrigger>
          <TabsTrigger value="transferred" className="data-[state=active]:bg-slate-600" data-testid="tab-transferred-orders">Aktarılan Siparişler</TabsTrigger>
          <TabsTrigger value="cancelled" className="data-[state=active]:bg-slate-600" data-testid="tab-cancelled-orders">İptal ve İade Siparişleri</TabsTrigger>
        </TabsList>

        {/* Shopify Orders Tab */}
        <TabsContent value="shopify" className="mt-6 space-y-4">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Toplam Sipariş</p>
                    <p className="text-2xl font-bold">{shopifyStats?.total_orders || 0}</p>
                  </div>
                  <ShoppingBag className="w-8 h-8 text-blue-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-green-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Nebim'e Aktarılan</p>
                    <p className="text-2xl font-bold text-green-600">{shopifyStats?.synced_to_nebim || 0}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-orange-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Bekleyen</p>
                    <p className="text-2xl font-bold text-orange-600">{shopifyStats?.pending_nebim || 0}</p>
                  </div>
                  <Clock className="w-8 h-8 text-orange-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Mağaza Sayısı</p>
                    <p className="text-2xl font-bold">{shopifyStores.length}</p>
                  </div>
                  <Store className="w-8 h-8 text-purple-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Action Bar */}
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex gap-2 items-end">
                  <div>
                    <Label className="text-xs text-slate-500">Başlangıç</Label>
                    <Input type="date" value={pullStartDate} onChange={(e) => setPullStartDate(e.target.value)} className="w-36" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Bitiş</Label>
                    <Input type="date" value={pullEndDate} onChange={(e) => setPullEndDate(e.target.value)} className="w-36" />
                  </div>
                </div>
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-xs text-slate-500">Mağaza</Label>
                  <Select value={shopifySelectedStore || "all"} onValueChange={(v) => setShopifySelectedStore(v === "all" ? "" : v)}>
                    <SelectTrigger><SelectValue placeholder="Tüm Mağazalar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tüm Mağazalar</SelectItem>
                      {shopifyStores.map(s => <SelectItem key={s.id} value={s.id}>{s.store_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="relative flex-1 min-w-[200px]">
                  <Label className="text-xs text-slate-500">Ara</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input placeholder="Sipariş no, müşteri..." value={shopifySearchQuery} onChange={(e) => setShopifySearchQuery(e.target.value)} className="pl-10" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={exportToExcel} className="text-green-600 border-green-200 hover:bg-green-50">
                    <FileSpreadsheet className="w-4 h-4 mr-2" />Excel'e Aktar
                  </Button>
                  <Button onClick={pullAndSyncOrders} disabled={syncingToNebim} className="bg-green-600 hover:bg-green-700">
                    {syncingToNebim ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <ArrowUpRight className="w-4 h-4 mr-2" />}
                    Çek ve Aktar
                  </Button>
                  <Button variant="outline" onClick={() => { fetchShopifyOrders(); fetchShopifyStats(); }} disabled={shopifyLoading}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${shopifyLoading ? 'animate-spin' : ''}`} />Yenile
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Selected Orders Action */}
          {selectedShopifyOrders.length > 0 && (
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <p className="font-medium">{selectedShopifyOrders.length} sipariş seçildi</p>
                  <Button onClick={syncSelectedToNebim} disabled={syncingToNebim} className="bg-blue-600 hover:bg-blue-700">
                    {syncingToNebim ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <ArrowUpRight className="w-4 h-4 mr-2" />}
                    Nebim'e Aktar
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Orders Table */}
          <Card>
            <CardContent className="pt-6">
              {shopifyLoading ? (
                <div className="flex items-center justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-slate-400" /></div>
              ) : shopifyOrders.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <ShoppingBag className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Sipariş bulunamadı</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b text-left">
                          <th className="pb-3 pl-2"><Checkbox checked={selectedShopifyOrders.length === shopifyOrders.length && shopifyOrders.length > 0} onCheckedChange={selectAllShopifyOrders} /></th>
                          <th className="pb-3 cursor-pointer hover:bg-slate-50" onClick={() => handleShopifySort('store_name')}>
                            <div className="flex items-center gap-1">Mağaza {shopifySortColumn === 'store_name' ? (shopifySortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}</div>
                          </th>
                          <th className="pb-3 cursor-pointer hover:bg-slate-50" onClick={() => handleShopifySort('order_name')}>
                            <div className="flex items-center gap-1">Sipariş No {shopifySortColumn === 'order_name' ? (shopifySortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}</div>
                          </th>
                          <th className="pb-3">Müşteri</th>
                          <th className="pb-3 cursor-pointer hover:bg-slate-50" onClick={() => handleShopifySort('created_at_shopify')}>
                            <div className="flex items-center gap-1">Tarih {shopifySortColumn === 'created_at_shopify' ? (shopifySortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}</div>
                          </th>
                          <th className="pb-3 cursor-pointer hover:bg-slate-50" onClick={() => handleShopifySort('total_price')}>
                            <div className="flex items-center gap-1">Tutar {shopifySortColumn === 'total_price' ? (shopifySortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}</div>
                          </th>
                          <th className="pb-3">Ödeme</th>
                          <th className="pb-3">Kargo</th>
                          <th className="pb-3">Nebim</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedShopifyOrders.map(order => (
                          <tr key={order.id} className="border-b hover:bg-slate-50">
                            <td className="py-3 pl-2"><Checkbox checked={selectedShopifyOrders.includes(order.id)} onCheckedChange={() => toggleShopifyOrderSelection(order.id)} /></td>
                            <td className="py-3"><Badge variant="outline" className="font-normal">{order.store_name}</Badge></td>
                            <td className="py-3 font-medium">{order.order_name}</td>
                            <td className="py-3">
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-slate-400" />
                                <div>
                                  <p className="text-sm">{order.customer?.first_name} {order.customer?.last_name}</p>
                                  <p className="text-xs text-slate-500">{order.customer?.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 text-sm">{formatDate(order.created_at_shopify)}</td>
                            <td className="py-3 font-medium">{formatCurrency(order.total_price, order.currency)}</td>
                            <td className="py-3"><Badge variant="outline" className={order.financial_status === 'paid' ? 'text-green-600' : 'text-yellow-600'}>{order.financial_status === 'paid' ? 'Ödendi' : order.financial_status}</Badge></td>
                            <td className="py-3"><Badge variant="outline" className={order.fulfillment_status === 'fulfilled' ? 'text-green-600' : 'text-yellow-600'}>{order.fulfillment_status === 'fulfilled' ? 'Gönderildi' : order.fulfillment_status || 'Hazırlanacak'}</Badge></td>
                            <td className="py-3">
                              {order.synced_to_nebim ? (
                                <div className="flex items-center gap-2">
                                  <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Aktarıldı</Badge>
                                  <Button variant="ghost" size="sm" onClick={() => unmarkSingleOrder(order.id)} className="h-6 w-6 p-0 text-slate-400 hover:text-red-500" title="İşareti kaldır">
                                    <RotateCcw className="w-3 h-3" />
                                  </Button>
                                </div>
                              ) : (
                                <Badge variant="outline" className="text-orange-600">Bekliyor</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-slate-500">Toplam {shopifyTotal} sipariş</p>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" disabled={shopifyPage === 1} onClick={() => setShopifyPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                      <span className="text-sm">Sayfa {shopifyPage} / {shopifyTotalPages}</span>
                      <Button variant="outline" size="sm" disabled={shopifyPage === shopifyTotalPages} onClick={() => setShopifyPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Other Tabs Content */}
        {activeTab !== 'shopify' && (
        <TabsContent value={activeTab} className="mt-6">
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">SİPARİŞ İŞLEMLERİ</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
                <div className="space-y-2">
                  <Label>Başlangıç Tarihi</Label>
                  <Input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                    data-testid="filter-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bitiş Tarihi</Label>
                  <Input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                    data-testid="filter-end-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sipariş Durumu</Label>
                  <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
                    <SelectTrigger data-testid="filter-status">
                      <SelectValue placeholder="Seçiniz" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tümü</SelectItem>
                      {orderStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Paketleme Durumu</Label>
                  <Select value={filters.package_status} onValueChange={(v) => setFilters({ ...filters, package_status: v === "all" ? "" : v })}>
                    <SelectTrigger data-testid="filter-package-status">
                      <SelectValue placeholder="Seçiniz" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tümü</SelectItem>
                      {packageStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2 col-span-2">
                  <Button variant="secondary" onClick={fetchOrders} data-testid="btn-filter-orders">
                    <Filter className="w-4 h-4 mr-2" />
                    Siparişleri Listele
                  </Button>
                </div>
              </div>

              {/* Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full dense-table">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="p-3 text-left">
                        <Checkbox
                          checked={selectedOrders.length === orders.length && orders.length > 0}
                          onCheckedChange={toggleSelectAll}
                          data-testid="checkbox-select-all"
                        />
                      </th>
                      <th className="p-3 text-left text-sm font-medium text-slate-600">Sipariş ID</th>
                      <th className="p-3 text-left text-sm font-medium text-slate-600">Sipariş No</th>
                      <th className="p-3 text-left text-sm font-medium text-slate-600">Üye Adı</th>
                      <th className="p-3 text-left text-sm font-medium text-slate-600">Üye Soyadı</th>
                      <th className="p-3 text-left text-sm font-medium text-slate-600">Sipariş Tarihi</th>
                      <th className="p-3 text-left text-sm font-medium text-slate-600">Sipariş Durumu</th>
                      <th className="p-3 text-left text-sm font-medium text-slate-600">Paketleme Durumu</th>
                      <th className="p-3 text-left text-sm font-medium text-slate-600">Sipariş Kaynağı</th>
                      <th className="p-3 text-right text-sm font-medium text-slate-600">Toplam Tutar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={10} className="p-8 text-center">
                          <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                        </td>
                      </tr>
                    ) : orders.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="p-8 text-center text-slate-500">
                          Sipariş bulunamadı
                        </td>
                      </tr>
                    ) : (
                      orders.map((order) => (
                        <tr
                          key={order.id}
                          className={`border-t hover:bg-slate-50 ${selectedOrders.includes(order.id) ? "bg-slate-100" : ""}`}
                          data-testid={`order-row-${order.id}`}
                        >
                          <td className="p-3">
                            <Checkbox
                              checked={selectedOrders.includes(order.id)}
                              onCheckedChange={() => toggleOrderSelection(order.id)}
                              data-testid={`checkbox-order-${order.id}`}
                            />
                          </td>
                          <td className="p-3 text-sm font-mono">{order.id.slice(0, 8)}...</td>
                          <td className="p-3 text-sm font-medium">{order.order_no}</td>
                          <td className="p-3 text-sm">{order.customer_name}</td>
                          <td className="p-3 text-sm">{order.customer_surname}</td>
                          <td className="p-3 text-sm">{order.order_date}</td>
                          <td className="p-3">
                            <Badge className={`text-xs ${getStatusBadge(order.order_status)}`}>
                              {order.order_status}
                            </Badge>
                          </td>
                          <td className="p-3 text-sm">{order.package_status || "-"}</td>
                          <td className="p-3 text-sm">{order.order_source || "-"}</td>
                          <td className="p-3 text-sm text-right font-medium">
                            {order.total_amount.toLocaleString("tr-TR")} {order.currency}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-slate-500">
                  Toplam {total} sipariş, Sayfa {page}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                    data-testid="btn-prev-page"
                  >
                    Önceki
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={orders.length < 20}
                    onClick={() => setPage((p) => p + 1)}
                    data-testid="btn-next-page"
                  >
                    Sonraki
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
